/** Documented Google deeplink fallback for local previews and SDK load failures.
 * Production buttons use the official SDK in google-preferred-source.ts.
 * https://developers.google.com/search/docs/appearance/preferred-sources
 */
export const GOOGLE_FOLLOW_URL = "https://www.google.com/preferences/source?q=fridge.com"

/**
 * The named target. A name (never `_blank`) means a second click reuses the
 * same window instead of stacking a new one, which is what Google's script
 * does.
 */
export const PREFERRED_SOURCE_WINDOW_NAME = "fridge_preferred_source"

/** Google's cap on both edges of the dialog window. */
const MAX_EDGE = 600

/** Google's fraction of the available screen when it is smaller than the cap. */
const SCREEN_FRACTION = 0.9

/** Used only when a browser reports no screen at all. */
const FALLBACK_SCREEN: PreferredSourceScreen = { width: 1024, height: 768 }

/** The parts of `window.screen` the sizing actually reads. */
export interface PreferredSourceScreen {
  width: number
  height: number
  availWidth?: number
  availHeight?: number
}

/** The parts of `window` the opener actually touches. */
export interface PreferredSourceWindow {
  open: (url: string, target: string, features: string) => unknown
  screen?: PreferredSourceScreen
}

/**
 * Build the `window.open` feature string: Google's centered, resizable,
 * scrollable dialog window, capped at 600 x 600 and never larger than 90% of
 * the available screen. `popup=yes` asks for a real window rather than a tab.
 */
export function preferredSourcePopupFeatures(screen?: PreferredSourceScreen | null): string {
  const s = screen ?? FALLBACK_SCREEN
  const availableWidth = Math.max(1, s.availWidth || s.width || FALLBACK_SCREEN.width)
  const availableHeight = Math.max(1, s.availHeight || s.height || FALLBACK_SCREEN.height)
  const outerWidth = Math.max(1, s.width || availableWidth)
  const outerHeight = Math.max(1, s.height || availableHeight)

  const width = Math.floor(Math.min(MAX_EDGE, SCREEN_FRACTION * availableWidth))
  const height = Math.floor(Math.min(MAX_EDGE, SCREEN_FRACTION * availableHeight))
  const left = Math.max(0, Math.floor((outerWidth - width) / 2))
  const top = Math.max(0, Math.floor((outerHeight - height) / 2))

  return `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
}

/**
 * Open the preferred-sources flow in the centered popup.
 *
 * Returns false when blocked so the button can ask the reader to allow popups.
 * This fallback never navigates the original page.
 */
export function openPreferredSourcePopup(
  url: string = GOOGLE_FOLLOW_URL,
  win?: PreferredSourceWindow | null,
): boolean {
  const target = win ?? (typeof window === "undefined" ? null : (window as unknown as PreferredSourceWindow))
  if (!target || typeof target.open !== "function") return false

  let opened: unknown
  try {
    opened = target.open(url, PREFERRED_SOURCE_WINDOW_NAME, preferredSourcePopupFeatures(target.screen))
  } catch {
    return false
  }
  if (!opened) return false

  const focus = (opened as { focus?: () => void }).focus
  if (typeof focus === "function") {
    try {
      focus.call(opened)
    } catch {
      /* a cross-origin popup may refuse focus; it is open, which is what matters */
    }
  }
  return true
}

/** The parts of a click event the handler reads. */
export interface PreferredSourceClickEvent {
  button?: number
  metaKey?: boolean
  ctrlKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  defaultPrevented?: boolean
  preventDefault: () => void
}

/**
 * The shared click handler. Progressive enhancement, in this order:
 *
 *  1. A modified click (new tab, new window, middle button) or an already
 *     handled event is left entirely alone — the browser's own behaviour wins.
 *  2. Otherwise the popup is attempted, and ONLY if it really opened is
 *     `preventDefault()` called. The current page therefore never navigates.
 *  3. If the popup was blocked, default is left intact and the anchor's own
 *     `target="_blank" rel="noopener"` carries the reader to Google.
 *
 * Returns whether the popup was opened (and the default suppressed).
 */
export function handlePreferredSourceClick(
  event: PreferredSourceClickEvent,
  url: string = GOOGLE_FOLLOW_URL,
  win?: PreferredSourceWindow | null,
): boolean {
  if (event.defaultPrevented) return false
  if (typeof event.button === "number" && event.button !== 0) return false
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false
  if (!openPreferredSourcePopup(url, win)) return false
  event.preventDefault()
  return true
}
